'use client'

import { useRouter, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Menu, X } from 'lucide-react'

import { QueryProvider } from '@/providers/query-provider'
import { useAuthStore } from '@/stores/auth.store'

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      router.replace('/overview')
    }
  }, [isAuthenticated, router])

  if (isAuthenticated) {
    return null
  }

  const isLogin = pathname === '/login'

  return (
    <QueryProvider>
      <div className="min-h-screen bg-gradient-to-br from-[var(--color-surface)] to-[var(--color-bg-subtle)]">
        {/* Nav — same as landing page */}
        <header className="fixed top-0 inset-x-0 bg-[var(--color-surface)]/95 backdrop-blur-md shadow-sm z-50">
          <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 font-bold text-xl text-[var(--color-text)]">
              <Image src="/logo/paidogeraldo.png" alt="Pai do Geraldo" width={40} height={40} className="h-10 w-auto" />
              <span>Pai do Geraldo</span>
            </Link>

            {/* desktop */}
            <div className="hidden md:flex items-center gap-6">
              <Link href="/#features" className="nav-link text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary-brand)] transition">Recursos</Link>
              <Link href="/#benefits" className="nav-link text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary-brand)] transition">Beneficios</Link>
              <Link href="/#pricing" className="nav-link text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary-brand)] transition">Planos</Link>
              <div className="w-px h-5 bg-[var(--color-border-custom)] mx-1" />
              {isLogin ? (
                <Link href="/register" className="px-4 py-2 text-sm font-semibold rounded-lg bg-[var(--color-primary-brand)] text-white hover:bg-[var(--color-primary-hover)] transition">
                  Criar conta
                </Link>
              ) : (
                <Link href="/login" className="text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary-brand)] transition">
                  Entrar
                </Link>
              )}
            </div>

            {/* mobile toggle */}
            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>

          {/* mobile menu */}
          {menuOpen && (
            <div className="md:hidden flex flex-col gap-2 px-6 pb-4 bg-[var(--color-surface)] border-t">
              <Link href="/#features" onClick={() => setMenuOpen(false)} className="py-2 text-sm font-medium text-[var(--color-text)]">Recursos</Link>
              <Link href="/#benefits" onClick={() => setMenuOpen(false)} className="py-2 text-sm font-medium text-[var(--color-text)]">Beneficios</Link>
              <Link href="/#pricing" onClick={() => setMenuOpen(false)} className="py-2 text-sm font-medium text-[var(--color-text)]">Planos</Link>
              {isLogin ? (
                <Link href="/register" onClick={() => setMenuOpen(false)} className="py-2 text-sm font-semibold text-[var(--color-primary-brand)]">Criar conta</Link>
              ) : (
                <Link href="/login" onClick={() => setMenuOpen(false)} className="py-2 text-sm font-medium text-[var(--color-text)]">Entrar</Link>
              )}
            </div>
          )}
        </header>

        {/* Content */}
        <div className="flex min-h-screen items-center justify-center px-4 py-12 pt-28">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>

      </div>
    </QueryProvider>
  )
}
